import { Box, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { ReactComponent as CheckIcon } from "Components/_Shared/Icons/Assets/check-success.svg";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { getNameInitials } from "Infrastructure/Utils/ClientUtils";
import { useState } from "react";

type Variant = "xs" | "small" | "medium" | "large";
export type AvatarStatus = "success" | "error";

type Props = {
  readonly firstName: string | null | undefined;
  readonly surname: string | null | undefined;
  /** Used for the initials only when neither `firstName` nor `surname` is known. */
  readonly fullName?: string;
  /** Shown instead of the initials when known. */
  readonly imageUrl?: string | null;
  readonly variant?: Variant;
  readonly color?: "primary" | "secondary";
  readonly status?: AvatarStatus;
};

const avatarSize: Record<Variant, number> = {
  xs: 28,
  small: 32,
  medium: 40,
  large: 48,
};

const statusIconSize: Record<Variant, number> = {
  xs: 12,
  small: 14,
  medium: 16,
  large: 18,
};

const initialsFontSize: Record<Variant, number> = {
  xs: 12,
  small: 14,
  medium: 16,
  large: 18,
};

export const UserInitialsAvatar: React.FC<Props> = ({
  firstName,
  surname,
  fullName,
  imageUrl,
  color = "secondary",
  variant = "medium",
  status,
}) => {
  const initials = getNameInitials({ firstName, surname, fullName });
  const iconSize = statusIconSize[variant];
  const size = avatarSize[variant];
  // Keyed by URL so a new imageUrl gets another chance to load.
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const showImage = !!imageUrl && imageUrl !== failedImageUrl;

  return (
    <Box position="relative" display="inline-flex">
      <Flex
        alignItems="center"
        justifyContent="center"
        width={size}
        height={size}
        borderRadius="50%"
        overflow="hidden"
        sx={(theme) => ({
          border: `1px solid ${theme.colors.white}`,
          backgroundColor: color === "primary" ? theme.colors.brandLight : theme.colors.textTertiary,
        })}
      >
        {showImage ? (
          <Box
            component="img"
            src={imageUrl}
            alt={[firstName, surname].filter(Boolean).join(" ") || fullName || ""}
            width="100%"
            height="100%"
            sx={{ objectFit: "cover" }}
            onError={() => setFailedImageUrl(imageUrl)}
          />
        ) : (
          <Typography
            fontSize={initialsFontSize[variant]}
            lineHeight={0}
            fontWeight={600}
            sx={(theme) => ({ color: color === "primary" ? theme.colors.brandPrimary : theme.colors.textPrimary })}
          >
            {initials}
          </Typography>
        )}
      </Flex>
      {status && (
        <Flex
          position="absolute"
          bottom={0}
          right={0}
          width={iconSize}
          height={iconSize}
          borderRadius="50%"
          alignItems="center"
          justifyContent="center"
          bgcolor={status === "success" ? "success.main" : "error.main"}
          sx={(theme) => ({ border: `1.5px solid ${theme.colors.white}` })}
        >
          {status === "success" ? (
            <CheckIcon width={iconSize * 0.6} height={iconSize * 0.6} />
          ) : (
            <CloseIcon width={iconSize * 0.6} height={iconSize * 0.6} />
          )}
        </Flex>
      )}
    </Box>
  );
};
